import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import styled, { useTheme } from 'styled-components';
import Switch from 'react-switch';
import { useNavigate } from 'react-router-dom';
import {
  BodyText,
  Button,
  FlexColumn,
  FlexRow,
  HashLength,
  MultiSelectDropdown,
  SearchableDropdown,
  SvgIcon,
  useMatchMedia
} from '@make-software/cspr-ui';
import {
  ButtonsContainer,
  centerModalStyles,
  ErrorText,
  ErrorTextContainer,
  ModalContainer,
  StyledCaption,
  StyledCaptionText,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput,
  TOGGLE_ACTIVE_COLOR
} from 'app/components/home/components/create-modal-styles';
import { checkMaxLength, validateDomain } from 'app/validators';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { ClickKeyResponseType, ClickKeyStatus, UpdateClickKeyRequestType } from 'app/build-api';
import { AUTH_TOKEN, CASPER, CASPER_TEST, MAINNET, statusCode, TESTNET } from 'app/constants';
import { ExpectedErrors } from 'app/components/home/components/click-keys/click-key-container';
import { ErrorResult } from 'app/build-api/types';
import { useGetCloudKeys } from 'app/build-api/hooks/useGetCloudKeys';
import { SIGN_IN_PATH } from 'app/route/paths';
import { ProxyApi, REST_API, RPC_METHODS, STREAMING_API } from '../types';
import { WarningWindow } from '../../../../common/warning-window/warning-window';
import { formatKeysHash } from '../../../../utils/measures-format';

export interface DropdownItem {
  label: string;
  value: string;
}

export interface CreateClickKeyWindowProps {
  isOpen: boolean;
  keyToEdit: ClickKeyResponseType;
  title: string;
  confirmLabel: string;
  onUpdate: (id: string, data: UpdateClickKeyRequestType) => void;
  dismissLabel?: string;
  onDismiss: () => void;
  expectedError?: ExpectedErrors | undefined;
  unexpectedError?: ErrorResult | null;
  portalClass?: string;
}

const NETWORKS = [
  { value: MAINNET, label: CASPER },
  { value: TESTNET, label: CASPER_TEST }
];

const StyledAddDomain = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer'
  })
);

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    height: '30px'
  })
);

const DomainsTitle = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    position: 'relative',
    top: '15px'
  })
);

const ProxyContentColumn = styled(FlexColumn)(({ theme }) => theme.withMedia({}));

const StyledToggle = styled(FlexColumn)<{ isLast?: boolean }>(({ theme, isLast }) =>
  theme.withMedia({
    padding: '16px 0',
    borderTop: `1px solid ${theme.styleguideColors.borderSecondary}`,
    borderBottom: isLast ? `1px solid ${theme.styleguideColors.borderSecondary}` : 'none'
  })
);

const StyledToggleContent = styled(FlexRow)<{ isApiActive?: boolean }>(({ theme, isApiActive }) =>
  theme.withMedia({
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: isApiActive && '12px'
  })
);

const StyledActiveToggle = styled(StyledToggleContent)(({ theme, isApiActive }) =>
  theme.withMedia({
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: '12px',
    padding: '16px 0',
    borderTop: `1px solid ${theme.styleguideColors.borderSecondary}`,
    borderBottom: `1px solid ${theme.styleguideColors.borderSecondary}`
  })
);

const StyledMultiSelectDropdown = styled(MultiSelectDropdown)(({ theme }) =>
  theme.withMedia({
    '&.carousel': {
      div: {
        width: '20%',
        backgroundColor: 'red',
        border: '1ps solid red'
      }
    }
  })
);

const HeightBlock = styled.div(({ theme }) =>
  theme.withMedia({
    maxHeight: '200px',
    overflowY: 'scroll'
  })
);

export const EditClickKeyWindow = ({
  isOpen,
  title,
  keyToEdit,
  confirmLabel,
  onUpdate,
  dismissLabel,
  onDismiss,
  unexpectedError,
  expectedError,
  portalClass = 'portal'
}: CreateClickKeyWindowProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const screenType = useMatchMedia(['mobile', 'tabletOrAbove'], []);
  const streamingApiMap = Object.fromEntries(STREAMING_API.map(({ value, label }) => [value, label]));
  const restApiMap = Object.fromEntries(REST_API.map(({ value, label }) => [value, label]));
  const rpcMethodsMap = Object.fromEntries(RPC_METHODS.map(({ value, label }) => [value, label]));
  const modalStyle = {
    overlay: {
      backgroundColor: theme.styleguideColors.backgroundOverlay,
      zIndex: 15
    },
    content: {
      ...centerModalStyles,
      ...{
        border: 'none',
        backgroundColor: theme.styleguideColors.backgroundPrimary,
        borderTop: `4px solid ${theme.styleguideColors.contentRed}`,
        borderColor: theme.styleguideColors.contentRed,
        boxShadow: '0px 16px 48px rgba(26, 25, 25, 0.2)',
        maxHeight: screenType === 'tabletOrAbove' ? '700px' : 'auto',
        overflow: 'auto'
      }
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
  }, [isOpen]);

  const {
    id,
    name,
    status,
    domains: domainsToEdit,
    app_id,
    network: networkToEdit,
    linked_cloud_key,
    cloud_key_settings
  } = keyToEdit;

  const incomeDomains = domainsToEdit.map((domain) => {
    return { value: domain };
  });

  const incomeNetwork = networkToEdit === MAINNET ? NETWORKS[0] : NETWORKS[1];

  const [showModal, setShowModal] = useState<boolean>(false);
  const [keyName, setKeyName] = useState<string>(name);
  const [isStatusActive, setIsStatusActive] = useState<boolean>(status === ClickKeyStatus.ACTIVE);
  const [network, setNetwork] = useState<DropdownItem>(incomeNetwork);

  const [streamingApi, setStreamingApi] = useState<string>(cloud_key_settings.streaming_endpoints);
  const [restApi, setRestApi] = useState<string>(cloud_key_settings.cloud_api_endpoints);
  const [rpcApi, setRpcApi] = useState<string>(cloud_key_settings.rpc_methods);

  const [domains, setDomains] = useState<{ value: string }[]>(incomeDomains);
  const [formErrors, setFormErrors] = useState<
    Record<
      'keyName' | 'domain' | 'streamingApi' | 'restApi' | 'rpcApi',
      string | null | string | string | string
    >
  >({
    keyName: '',
    domain: '',
    streamingApi: '',
    restApi: '',
    rpcApi: ''
  });

  const [isStreamingAPIActive, setIsStreamingAPIActive] = useState<boolean>(
    cloud_key_settings?.streaming
  );
  const [isRestAPIActive, setIsRestAPIActive] = useState<boolean>(
    cloud_key_settings.cloud_api_proxy
  );
  const [isRpcAPIActive, setIsRpcAPIActive] = useState<boolean>(cloud_key_settings.rpc_proxy);

  const { data: cloudKeys, error: fetchingKeysError, httpCode } = useGetCloudKeys(1, 100);

  const [linkedCloudKey, setLinkedCloudKey] = useState<DropdownItem>({
    value: linked_cloud_key.id,
    label: `${linked_cloud_key.name}, ${formatKeysHash(linked_cloud_key.hash, HashLength.TINY)}`
  });

  const [linkedCloudKeysList, setLinkedCloudKeysList] = useState<DropdownItem[]>([
    { value: '', label: '' }
  ]);

  useEffect(() => {
    if (cloudKeys?.data) {
      const formattedKeys = cloudKeys?.data.map((k) => {
        const hashStart = k.hash.slice(0, 4);
        const hashEnd = k.hash.slice(k.hash.length - 4);
        return {
          value: k.id,
          label: `${k.name}, ${hashStart}...${hashEnd}`
        };
      });

      setLinkedCloudKeysList(formattedKeys);
    }
  }, [cloudKeys]);

  if (fetchingKeysError && httpCode === statusCode.authentication_error) {
    localStorage.removeItem(AUTH_TOKEN);
    navigate(SIGN_IN_PATH);
  }

  const clearForm = () => {
    setKeyName(name);
    setNetwork(incomeNetwork);
    setDomains(incomeDomains);
    setStreamingApi('');
    setRestApi('');
    setRpcApi('');
    setIsStatusActive(status === ClickKeyStatus.ACTIVE);
    setFormErrors({
      ...formErrors,
      keyName: '',
      domain: '',
      streamingApi: '',
      restApi: '',
      rpcApi: ''
    });
  };

  useEffect(() => {
    !isOpen && clearForm();
    setShowModal(isOpen);
  }, [isOpen]);

  const handleKeyName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setKeyName(val);
  };

  const handleKeyNameOnBlur = () => {
    setFormErrors({
      ...formErrors,
      keyName: !keyName.length ? 'This field is required' : checkMaxLength(keyName, 50)
    });
  };

  const handleLinkCloudKey = (linkedKey: DropdownItem) => {
    setLinkedCloudKey(linkedKey);
  };

  const handleAddDomain = () => {
    if (domains.length >= 5) return;
    setDomains([...domains, { value: '' }]);
  };

  const handleDomainOnBlur = (index: number) => {
    const data: any = [...domains];
    const value = data[index].value;
    setFormErrors({
      ...formErrors,
      domain: !value.length ? 'This field is required' : validateDomain(value)
    });
  };

  const handleToggleStreamingAPI = () => {
    setIsStreamingAPIActive((prevState) => !prevState);
    setStreamingApi('');
    setFormErrors({
      ...formErrors,
      streamingApi: ''
    });
  };

  const handleToggleRestAPI = () => {
    setIsRestAPIActive((prevState) => !prevState);
    setRestApi('');
    setFormErrors({
      ...formErrors,
      restApi: ''
    });
  };

  const handleToggleRpcAPI = () => {
    setIsRpcAPIActive((prevState) => !prevState);
    setRpcApi('');
    setFormErrors({
      ...formErrors,
      rpcApi: ''
    });
  };

  const handleSelectProxyApi = (event: any, key: ProxyApi) => {
    const selectedItems = event.target.value.map((d: any) => d.value).join(',');
    const hasSelection = !!selectedItems;

    const stateSetters: Record<ProxyApi, React.Dispatch<React.SetStateAction<string>>> = {
      [ProxyApi.streaming]: setStreamingApi,
      [ProxyApi.rest]: setRestApi,
      [ProxyApi.rpc]: setRpcApi
    };

    const errorKeys: Record<ProxyApi, keyof typeof formErrors> = {
      [ProxyApi.streaming]: 'streamingApi',
      [ProxyApi.rest]: 'restApi',
      [ProxyApi.rpc]: 'rpcApi'
    };

    const setState = stateSetters[key];
    const errorKey = errorKeys[key];

    setState(hasSelection ? selectedItems : '');

    setFormErrors((prev) => ({
      ...prev,
      [errorKey]: hasSelection ? '' : 'This field is required'
    }));
  };

  const handleConfirm = () => {
    if (!keyName.length) {
      setFormErrors({
        ...formErrors,
        keyName: 'This field is required'
      });
      return;
    }

    if (!domains[0].value) {
      setFormErrors({
        ...formErrors,
        domain: 'This field is required'
      });
      return;
    }

    if (isStreamingAPIActive && !streamingApi.length) {
      setFormErrors({
        ...formErrors,
        streamingApi: 'This field is required'
      });
      return;
    }

    if (isRestAPIActive && !restApi.length) {
      setFormErrors({
        ...formErrors,
        restApi: 'This field is required'
      });
      return;
    }

    if (isRpcAPIActive && !rpcApi.length) {
      setFormErrors({
        ...formErrors,
        rpcApi: 'This field is required'
      });
      return;
    }

    const invalidDomain = domains
      .map((d) => d.value)
      .filter((d) => d)
      .filter((d) => validateDomain(d));

    !formErrors.keyName &&
      !formErrors.domain &&
      !invalidDomain.length &&
      !formErrors.streamingApi &&
      !formErrors.restApi &&
      !formErrors.rpcApi &&
      onUpdate &&
      onUpdate(id, {
        network: network.value,
        name: keyName,
        domains: domains.map((d) => d.value).filter((d) => d),
        status: isStatusActive ? ClickKeyStatus.ACTIVE : ClickKeyStatus.INACTIVE,
        cloud_key_id: linkedCloudKey.value,
        cloud_key_settings: {
          rpc_proxy: isRpcAPIActive,
          rpc_methods: isRpcAPIActive ? rpcApi : '',
          streaming: isStreamingAPIActive,
          streaming_endpoints: isStreamingAPIActive ? streamingApi : '',
          cloud_api_proxy: isRestAPIActive,
          cloud_api_endpoints: isRestAPIActive ? restApi : ''
        }
      });
  };

  const handleOnDismiss = () => {
    clearForm();
    onDismiss();
  };

  const handleChangeKeyStatus = () => {
    setIsStatusActive((prevState) => !prevState);
  };

  const handleChangeDomain = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = event.target;
    const data: any = [...domains];
    data[index].value = value;
    setDomains(data);
  };

  const handleDelete = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const data = [...domains];
    data.splice(index, 1);
    setDomains(data);
  };

  const parseCommaSeparatedValues = (input: string, valueLabelMap: Record<string, string>) => {
    if (!input) return [];

    return input.split(',').map((item) => {
      const value = item.trim();
      const label = valueLabelMap[value] || value; // Use the predefined label or fallback to the value

      return { value, label };
    });
  };

  return (
    <>
      {showModal && (
        <ReactModal
          isOpen={showModal}
          style={modalStyle}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick={false}
          portalClassName={portalClass}
        >
          <ModalContainer gap={20}>
            {unexpectedError ? (
              <ErrorWindow
                title={unexpectedError.code || ''}
                information={unexpectedError.message}
                withModal
                icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
              />
            ) : (
              <>
                <StyledCaption>
                  <StyledCaptionText size={1} scale="lg">
                    {title}
                  </StyledCaptionText>
                </StyledCaption>
                <StyledActiveToggle>
                  <BodyText size={1}>Active status:</BodyText>
                  <Switch
                    checked={isStatusActive}
                    onChange={handleChangeKeyStatus}
                    onColor={TOGGLE_ACTIVE_COLOR}
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                </StyledActiveToggle>
                <>
                  <FlexRow justify="center">
                    <StyledInput
                      value={app_id}
                      label={<BodyText size={1}>AppID</BodyText>}
                      placeholder="AppID"
                      disabled
                      withBorder
                    />
                  </FlexRow>
                  <FlexRow justify="center">
                    <StyledInput
                      value={keyName}
                      label={<BodyText size={1}>Name</BodyText>}
                      placeholder="Key name"
                      onChange={handleKeyName}
                      onBlur={handleKeyNameOnBlur}
                      error={!!formErrors.keyName}
                      validationText={formErrors.keyName}
                    />
                  </FlexRow>
                  <DomainsTitle size={1}>Domains (up to 5)</DomainsTitle>
                  {domains.map((d, index) => (
                    <>
                      <FlexRow justify={'center'} align={'center'} gap={5}>
                        <StyledInput
                          name={'domain'}
                          value={d.value}
                          placeholder="example.com"
                          onChange={(event) => handleChangeDomain(event, index)}
                          onBlur={() => index === 0 && handleDomainOnBlur(index)}
                          error={
                            index === 0
                              ? !!formErrors.domain
                              : Boolean(
                                  domains[index].value && validateDomain(domains[index].value)
                                )
                          }
                          validationText={
                            index === 0
                              ? formErrors.domain
                              : domains[index].value && validateDomain(domains[index].value)
                          }
                        />
                        {index !== 0 && (
                          <StyledButton
                            width={'100'}
                            color={'secondaryBlue'}
                            onClick={(event) => handleDelete(index, event)}
                          >
                            Remove
                          </StyledButton>
                        )}
                      </FlexRow>
                    </>
                  ))}
                  <BodyText
                    size={3}
                    scale={'sm'}
                    variation={domains.length === 5 ? 'gray' : 'blue'}
                  >
                    <StyledAddDomain onClick={handleAddDomain}>
                      + Add another domain
                    </StyledAddDomain>
                  </BodyText>
                  <FlexRow justify="center">
                    <StyledInput
                      value={incomeNetwork.label}
                      label={<BodyText size={1}>Network</BodyText>}
                      placeholder="Network"
                      disabled
                      withBorder
                    />
                  </FlexRow>
                  {expectedError && Object.keys(expectedError).length !== 0 && (
                    <ErrorTextContainer justify={'center'}>
                      <ErrorText size={3}>{expectedError.duplicateDomainError}</ErrorText>
                    </ErrorTextContainer>
                  )}
                  <BodyText size={1}>Connected CSPR.cloud key</BodyText>
                  <SearchableDropdown
                    value={linkedCloudKey}
                    items={linkedCloudKeysList}
                    onSelect={handleLinkCloudKey}
                    height={'auto'}
                    fontSize={'14px'}
                    maxHeight={'134px'}
                    placeholder={'Please link CSPR.cloud key...'}
                    isError={Boolean(fetchingKeysError)}
                  />
                </>

                <WarningWindow
                  message={
                    <>
                      These switches control frontend access to{' '}
                      <BodyText size={1}>CSPR.cloud endpoints.</BodyText> Enable only when needed
                      and <BodyText size={1}>whitelist required endpoints</BodyText> to prevent
                      quota abuse.
                    </>
                  }
                />

                <ProxyContentColumn>
                  <StyledToggle>
                    <StyledToggleContent isApiActive={isStreamingAPIActive}>
                      <BodyText size={1}>Streaming API</BodyText>
                      <Switch
                        checked={isStreamingAPIActive}
                        onChange={handleToggleStreamingAPI}
                        onColor={TOGGLE_ACTIVE_COLOR}
                        checkedIcon={false}
                        uncheckedIcon={false}
                      />
                    </StyledToggleContent>
                    {isStreamingAPIActive && (
                      <>
                        <HeightBlock>
                          <StyledMultiSelectDropdown
                            placeholder="Select endpoint..."
                            value={parseCommaSeparatedValues(streamingApi, streamingApiMap)}
                            items={STREAMING_API}
                            onSelect={(newValues) =>
                              handleSelectProxyApi(newValues, ProxyApi.streaming)
                            }
                          />
                        </HeightBlock>
                        {formErrors.streamingApi && !streamingApi.length && (
                          <ErrorTextContainer justify={'center'} top={'42px'}>
                            <ErrorText size={3}>Please select Streaming API</ErrorText>
                          </ErrorTextContainer>
                        )}
                      </>
                    )}
                  </StyledToggle>
                  <StyledToggle>
                    <StyledToggleContent isApiActive={isRestAPIActive}>
                      <BodyText size={1}>REST API</BodyText>
                      <Switch
                        checked={isRestAPIActive}
                        onChange={handleToggleRestAPI}
                        onColor={TOGGLE_ACTIVE_COLOR}
                        checkedIcon={false}
                        uncheckedIcon={false}
                      />
                    </StyledToggleContent>
                    {isRestAPIActive && (
                      <>
                        <HeightBlock>
                          <StyledMultiSelectDropdown
                            placeholder="Select endpoint..."
                            value={parseCommaSeparatedValues(restApi, restApiMap)}
                            items={REST_API}
                            onSelect={(newValues) => handleSelectProxyApi(newValues, ProxyApi.rest)}
                          />
                        </HeightBlock>
                        {formErrors.restApi && !restApi.length && (
                          <ErrorTextContainer justify={'center'} top={'42px'}>
                            <ErrorText size={3}>Please select Rest API</ErrorText>
                          </ErrorTextContainer>
                        )}
                      </>
                    )}
                  </StyledToggle>
                  <StyledToggle isLast>
                    <StyledToggleContent isApiActive={isRpcAPIActive}>
                      <BodyText size={1}>RPC node</BodyText>
                      <Switch
                        checked={isRpcAPIActive}
                        onChange={handleToggleRpcAPI}
                        onColor={TOGGLE_ACTIVE_COLOR}
                        checkedIcon={false}
                        uncheckedIcon={false}
                      />
                    </StyledToggleContent>
                    {isRpcAPIActive && (
                      <>
                        <HeightBlock>
                          <StyledMultiSelectDropdown
                            placeholder="Select method..."
                            value={parseCommaSeparatedValues(rpcApi, rpcMethodsMap)}
                            items={RPC_METHODS}
                            onSelect={(newValues) => handleSelectProxyApi(newValues, ProxyApi.rpc)}
                          />
                        </HeightBlock>
                        {formErrors.rpcApi && !rpcApi.length && (
                          <ErrorTextContainer justify={'center'} top={'42px'}>
                            <ErrorText size={3}>Please select RPC method</ErrorText>
                          </ErrorTextContainer>
                        )}
                      </>
                    )}
                  </StyledToggle>
                </ProxyContentColumn>
                <ButtonsContainer justify={'space-between'}>
                  {dismissLabel && (
                    <StyledDismissButton color={'utility'} onClick={handleOnDismiss}>
                      {dismissLabel}
                    </StyledDismissButton>
                  )}
                  <StyledConfirmButton onClick={handleConfirm}>{confirmLabel}</StyledConfirmButton>
                </ButtonsContainer>
              </>
            )}
          </ModalContainer>
        </ReactModal>
      )}
    </>
  );
};

export default EditClickKeyWindow;
